/**
 * The capability token for `/member-view/:token`, and what it means for the
 * rest of the SPA.
 *
 * Its own module rather than a corner of `api.ts` because BOTH request paths
 * need it and they are separate files: `api.ts` puts the token on reads, and
 * `dispatch.ts` — which has its own `bearer()` and never saw this token —
 * needs to know the page is a capability view so it can decline writes in
 * words rather than let them fail as an HTTP 401.
 *
 * Module-level rather than context: `bearer()` is a plain function called from
 * every read on the page, and threading a provider through all of them would
 * touch far more than this feature earns.
 */

import { supabase } from "./supabase";

let capabilityToken: string | null = null;

/**
 * Set by the MemberView route on mount and cleared on unmount, so the token
 * cannot leak into a staff session opened in the same tab afterwards.
 */
export function setCapabilityToken(token: string | null): void {
  capabilityToken = token;
}

export function currentCapabilityToken(): string | null {
  return capabilityToken;
}

/**
 * The Authorization bearer for ANY call this SPA makes — the capability token
 * when a capability view is on screen, the Supabase session otherwise.
 *
 * ONE function, because three copies of it produced three separate bugs.
 * `api.ts` had the capability-first version; `dispatch.ts` had a Supabase-only
 * copy, which sent an EMPTY header from the framed member page and surfaced as
 * "Couldn't register the member with stripe: HTTP 401" on Buy and Book
 * (2026-09-11); `use-saga-run.ts` had a third copy, still Supabase-only, which
 * sent an empty header on the completion poll — so a cancel dispatched fine and
 * then reported "this account was refused permission to check its progress
 * (whoami_unauthorized)" (Mario, 2026-09-12). Same defect, three times, because
 * the rule lived in three places instead of one.
 *
 * The order is not arbitrary and the two can never both be set: a capability
 * view has no Supabase session to fall through to, and a staff session never
 * sets a capability token (MemberView clears it on unmount).
 *
 * On `main` this is inert — nothing sets a capability token there, so every
 * caller gets the Supabase session exactly as before.
 */
export async function bearerToken(): Promise<string> {
  const cap = currentCapabilityToken();
  if (cap !== null) return cap;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? "";
}

/**
 * True while a capability view is on screen. The page is then READ-ONLY by
 * construction: the token authorises one member's two read endpoints and
 * nothing else, so every write and every studio-level read would 401.
 */
export function isCapabilityView(): boolean {
  return capabilityToken !== null;
}

/**
 * Tell the framing page that this view just re-read itself.
 *
 * The demo's studio pane draws a five-second ring beside its URL chip, and the
 * ring has to mean something: it is reset by this message, so it reports the
 * refresh that actually happened rather than running a decorative timer beside
 * it. No data crosses — the event IS the payload, which is why `"*"` is a safe
 * target — and nothing happens at all outside a capability view.
 */
export function announceCapabilityRefresh(): void {
  if (capabilityToken === null) return;
  try {
    window.parent.postMessage({ type: "clavis-member-view-refreshed" }, "*");
  } catch {
    // No parent to tell (the page opened directly) is not an error.
  }
}

/**
 * True on the Clavis demo build, whose SERVER accepts the capability token as a
 * staff stand-in (see `configureDemoStaffStandIn` in the dashboard's auth.ts).
 * There, the two guards below are no longer telling the truth: the writes DO
 * succeed, so refusing them in the browser would be the only thing stopping
 * them — which is the opposite of what the comments here promise, namely that
 * the client is the wording and the server is the protection.
 *
 * `LAYER_PUB_`, not `VITE_`: web/vite.config.ts narrows `envPrefix` to
 * `LAYER_PUB_`, so a `VITE_`-prefixed var is invisible to this SPA. It is a
 * boolean, never a secret — that prefix is client-visible by design — and it is
 * set only on the demo's own Railway service, built off the `clavis-demo`
 * branch.
 */
export const DEMO_STAFF_STANDIN: boolean =
  import.meta.env["LAYER_PUB_DEMO_STAFF_STANDIN"] === "1";

/**
 * What a visitor is told instead of an HTTP status.
 *
 * The demo frames the studio's REAL dashboard, so the controls are real too —
 * Book, Buy, Refund, Edit. Letting them through to a 401 showed
 * "HTTP 401 returned no message (whoami_unauthorized)" in the middle of a
 * marketing page, which reads as a broken product rather than a guarded one.
 * The guard is the point: a page strangers share must not be able to issue a
 * refund. So the surface says so plainly.
 */

export const SANDBOX_READ_ONLY_CODE = "sandbox_read_only";
export const SANDBOX_READ_ONLY_MESSAGE =
  "Read-only in the sandbox — this is the studio's real dashboard, but changes are disabled on a shared demo.";
